import React, { useState, useEffect } from 'react';
import { MdClose, MdSave, MdAdd, MdAutoFixHigh } from 'react-icons/md';
import { RubricItemCard } from '../Assignments/RubricBuilder';
import {
  getRubricsByAssignment,
  createRubric,
  updateRubric,
  getComponentQuestions
} from '../../utils/api/AssignmentsApi';

const ReadOnlyItem = ({ item, idx }) => (
  <div className="border border-gray-200 rounded-lg bg-white p-4">
    <div className="flex items-center justify-between mb-2">
      <h4 className="text-sm font-semibold text-gray-900 m-0">{item.title || `Item ${idx + 1}`}</h4>
      <span className="text-xs font-medium text-gray-600">{item.max_points} pts</span>
    </div>
    {item.description && (
      <p className="text-xs text-gray-600 m-0">{item.description}</p>
    )}
    {item.category && item.category !== 'general' && (
      <span className="inline-block mt-2 px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
        {item.category}
      </span>
    )}
    {item.grading_type === 'tiered' && item.point_tiers?.length > 0 && (
      <div className="mt-2 space-y-1">
        {item.point_tiers.map((tier, tIdx) => (
          <div key={tIdx} className="flex gap-2 text-xs text-gray-500 pl-2 border-l-2 border-gray-200">
            <span className="font-medium">{tier.points}pts</span>
            {tier.label && <span className="font-medium text-gray-700">({tier.label})</span>}
            {tier.criteria && <span>{tier.criteria}</span>}
          </div>
        ))}
      </div>
    )}
  </div>
);

const RubricSection = ({ rubric, isInstructor, isHighlighted, onItemChange, onItemDelete, onAddItem, unlinkedQuestions, onImportUnlinked }) => {
  const items = rubric?.items || [];
  const totalPoints = items.reduce((sum, i) => sum + (i.max_points || 0), 0);

  return (
    <div className={`rounded-lg ${isHighlighted ? 'ring-2 ring-blue-300 bg-blue-50/30' : ''}`}>
      <div className="flex items-center justify-between px-3 py-2">
        <h4 className="text-sm font-semibold text-gray-900 m-0">{rubric.title || 'Rubric'}</h4>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">{items.length} items</span>
          <span className="text-xs font-medium text-gray-700">{totalPoints} pts</span>
        </div>
      </div>

      {/* Unlinked questions banner */}
      {isInstructor && unlinkedQuestions?.length > 0 && (
        <div className="mx-3 mb-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-2">
          <MdAutoFixHigh className="text-amber-600 text-base flex-shrink-0" />
          <span className="text-xs text-amber-800 flex-1">
            {unlinkedQuestions.length} question{unlinkedQuestions.length > 1 ? 's' : ''} not linked
          </span>
          <button
            onClick={onImportUnlinked}
            className="px-2.5 py-1 text-xs font-medium text-amber-700 bg-amber-100 border border-amber-300 rounded-lg hover:bg-amber-200 whitespace-nowrap"
          >
            Import
          </button>
        </div>
      )}

      <div className="px-3 pb-3 space-y-2">
        {items.length === 0 && (
          <div className="text-center py-4 text-gray-400 text-xs">
            {isInstructor ? 'No items yet. Click below to add one.' : 'No rubric items for this section.'}
          </div>
        )}

        {items.map((item, idx) => (
          isInstructor && onItemChange ? (
            <RubricItemCard
              key={item.item_id || idx}
              item={item}
              index={idx}
              onChange={(updated) => onItemChange(idx, updated)}
              onDelete={() => onItemDelete(idx)}
            />
          ) : (
            <ReadOnlyItem key={item.item_id || idx} item={item} idx={idx} />
          )
        ))}

        {isInstructor && onAddItem && (
          <button
            onClick={onAddItem}
            className="flex items-center gap-2 w-full justify-center px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
          >
            <MdAdd /> Add Item
          </button>
        )}
      </div>
    </div>
  );
};

const RubricSidebar = ({ assignmentId, componentId, classContext, isOpen, onClose, isInstructor }) => {
  const [allRubrics, setAllRubrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);
  const [unlinkedQuestions, setUnlinkedQuestions] = useState([]);

  useEffect(() => {
    if (!isOpen || !assignmentId) return;

    const loadRubrics = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getRubricsByAssignment(assignmentId, classContext);
        const rubricList = data.rubrics || [];

        // Show: overall rubric (no component_id) + current component's rubric
        const relevant = rubricList.filter(r =>
          !r.component_id || r.component_id === componentId
        );

        // Sort: overall first, then component
        relevant.sort((a, b) => {
          if (!a.component_id && b.component_id) return -1;
          if (a.component_id && !b.component_id) return 1;
          return 0;
        });

        setAllRubrics(relevant.length > 0 ? relevant : [{ title: 'Rubric', items: [], _isNew: true, component_id: componentId || null }]);

        // Check for unlinked questions (instructor only)
        if (isInstructor && componentId) {
          try {
            const qResult = await getComponentQuestions(assignmentId, componentId, classContext);
            const questions = qResult.questions || [];

            const linkedIds = new Set();
            relevant.forEach(r => {
              (r.items || []).forEach(item => {
                (item.linked_questions || []).forEach(lq => linkedIds.add(lq.question_id));
              });
            });

            const unlinked = questions.filter(q => !linkedIds.has(q.question_id));
            setUnlinkedQuestions(unlinked);
          } catch {
            setUnlinkedQuestions([]);
          }
        }
      } catch (err) {
        console.error('Error loading rubric:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadRubrics();
  }, [isOpen, assignmentId, componentId]);

  const handleItemChange = (rubricIdx, itemIdx, updatedItem) => {
    setAllRubrics(prev => {
      const updated = [...prev];
      const rubric = { ...updated[rubricIdx] };
      const newItems = [...(rubric.items || [])];
      newItems[itemIdx] = updatedItem;
      rubric.items = newItems;
      updated[rubricIdx] = rubric;
      return updated;
    });
  };

  const handleItemDelete = (rubricIdx, itemIdx) => {
    setAllRubrics(prev => {
      const updated = [...prev];
      const rubric = { ...updated[rubricIdx] };
      rubric.items = (rubric.items || []).filter((_, i) => i !== itemIdx);
      updated[rubricIdx] = rubric;
      return updated;
    });
  };

  const addItem = (rubricIdx) => {
    const newItem = {
      item_id: `item_${Date.now()}`,
      order: (allRubrics[rubricIdx]?.items || []).length,
      category: 'general',
      title: '',
      description: '',
      max_points: 0,
      grading_type: 'all_or_nothing',
      point_tiers: [],
      example_feedback: '',
      linked_questions: [],
      is_active: true
    };
    setAllRubrics(prev => {
      const updated = [...prev];
      const rubric = { ...updated[rubricIdx] };
      rubric.items = [...(rubric.items || []), newItem];
      updated[rubricIdx] = rubric;
      return updated;
    });
  };

  const handleImportUnlinked = (rubricIdx) => {
    if (unlinkedQuestions.length === 0) return;

    const rubric = allRubrics[rubricIdx];
    const newItems = unlinkedQuestions.map((q, idx) => ({
      item_id: `item_${Date.now()}_${idx}`,
      order: (rubric?.items || []).length + idx,
      category: 'question',
      title: (q.question_text || '').substring(0, 80) || `Question ${idx + 1}`,
      description: q.question_text || '',
      max_points: q.question_type === 'mcq' ? 1 : 5,
      grading_type: 'all_or_nothing',
      point_tiers: [],
      example_feedback: '',
      linked_questions: [{
        question_id: q.question_id,
        question_type: q.question_type,
        source: q.source,
        source_id: q.source_id || '',
        auto_grade: q.question_type === 'mcq'
      }],
      is_active: true
    }));

    setAllRubrics(prev => {
      const updated = [...prev];
      const r = { ...updated[rubricIdx] };
      r.items = [...(r.items || []), ...newItems];
      updated[rubricIdx] = r;
      return updated;
    });
    setUnlinkedQuestions([]);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSaveMessage(null);

      for (const rubric of allRubrics) {
        const rubricData = {
          assignment_id: assignmentId,
          title: rubric.title || 'Rubric',
          description: rubric.description || '',
          scoring_mode: rubric.scoring_mode || 'positive',
          items: (rubric.items || []).map((item, idx) => ({ ...item, order: idx })),
          component_id: rubric.component_id || null
        };

        if (rubric.rubric_id && !rubric._isNew) {
          await updateRubric(rubric.rubric_id, rubricData, classContext);
        } else if (rubric.items && rubric.items.length > 0) {
          const result = await createRubric(rubricData, classContext);
          if (result.success && result.rubric_id) {
            rubric.rubric_id = result.rubric_id;
            rubric._isNew = false;
          }
        }
      }

      setSaveMessage('Saved');
      setTimeout(() => setSaveMessage(null), 2000);
    } catch (err) {
      console.error('Error saving rubric:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const totalItems = allRubrics.reduce((sum, r) => sum + (r.items || []).length, 0);
  const totalPoints = allRubrics.reduce((sum, r) =>
    sum + (r.items || []).reduce((s, i) => s + (i.max_points || 0), 0), 0);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Sidebar */}
      <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-gray-50 shadow-2xl z-50 overflow-hidden flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-lg font-bold text-gray-900 m-0">Rubric</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-gray-500">{totalItems} items</span>
              <span className="text-sm font-medium text-gray-700">{totalPoints} pts</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {saveMessage && <span className="text-xs text-green-600 font-medium">{saveMessage}</span>}
            {isInstructor && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                <MdSave className="text-sm" />
                {saving ? 'Saving...' : 'Save'}
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
            >
              <MdClose className="text-xl" />
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-4 mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-red-800 text-xs">
            {error}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center h-32 text-gray-500 text-sm">
              Loading rubric...
            </div>
          ) : (
            allRubrics.map((rubric, rIdx) => (
              <RubricSection
                key={rubric.rubric_id || `rubric-${rIdx}`}
                rubric={rubric}
                isInstructor={isInstructor}
                isHighlighted={!!rubric.component_id && rubric.component_id === componentId}
                onItemChange={isInstructor ? (itemIdx, updated) => handleItemChange(rIdx, itemIdx, updated) : null}
                onItemDelete={isInstructor ? (itemIdx) => handleItemDelete(rIdx, itemIdx) : null}
                onAddItem={isInstructor ? () => addItem(rIdx) : null}
                unlinkedQuestions={rubric.component_id === componentId ? unlinkedQuestions : []}
                onImportUnlinked={() => handleImportUnlinked(rIdx)}
              />
            ))
          )}
        </div>
      </div>

      <style>
        {`
          @keyframes slide-in-right {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
          .animate-slide-in-right {
            animation: slide-in-right 0.3s ease-out;
          }
        `}
      </style>
    </>
  );
};

export default RubricSidebar;
